import { useAuth0 } from "@auth0/auth0-react";
import { config } from "@config/config";
import {
  ArrowDownTrayIcon,
  BellAlertIcon,
  BellIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  CheckBadgeIcon,
  ClipboardIcon,
  Cog6ToothIcon,
  Cog8ToothIcon,
  DocumentDuplicateIcon,
  FolderPlusIcon,
  HomeIcon,
  NewspaperIcon,
  PencilSquareIcon,
  QuestionMarkCircleIcon,
  SquaresPlusIcon,
  UserCircleIcon,
  UserGroupIcon,
  UsersIcon,
  VideoCameraIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { FeatureFlagGate } from "@utils/FeatureFlags/FeatureFlagGate";
import { FeatureFlagPreviewer } from "@utils/FeatureFlags/FeatureFlagPreviewer";
import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import { hasTemplatesAccess } from "@utils/FeatureFlags/selfServeAccess";
import { useFlags, useLDClient, useLDClientError } from "launchdarkly-react-client-sdk";
import React, { useState } from "react";
import { GoSidebarCollapse, GoSidebarExpand } from "react-icons/go";
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";

import { Button, Headline, NavItem, SidebarLayout, Small, TopBarAction } from "@fronterahealth/frontera-ui-components";

import { EHRConnect } from "@components/EHRConnect/ERHConnect";
import { EhrMfaStrip } from "@components/EHRConnect/EhrMfaStrip";
import { NotificationBell } from "@components/NotificationBell/NotificationBell";
import { useAdminData } from "@providers/AdminDataProvider";
import { EHRConnectionProvider } from "@providers/EHRConnectionProvider";

import { AccessTokenRetriever } from "./AccessTokenRetriever";
import { EnvironmentMarker } from "./components/EnvironmentMarker";
import { MenuItemRenderer } from "./components/MenuItemRenderer";
import { TooltipFocusGuard } from "./components/TooltipFocusGuard";
import { AllRoutes, homeRolesForFlags, resolveRootRedirect, roleMapping, roleType } from "./config";

enum MenuSection {
  CLINICAL_TOOLS = "Clinical Tools",
  RESOURCES = "Resources",
  USER_SETTINGS = "User Settings",
  ORGANIZATION_SETTINGS = "Organization Settings",
}

const MenuSections = [
  MenuSection.CLINICAL_TOOLS,
  MenuSection.RESOURCES,
  MenuSection.USER_SETTINGS,
  MenuSection.ORGANIZATION_SETTINGS,
] as const;
type MenuSectionsType = (typeof MenuSections)[number];

declare global {
  interface Window {
    FreshworksWidget: (action: string, target?: string) => void;
  }
}

export interface MenuItem {
  path: AllRoutes;
  name: string;
  section: MenuSectionsType;
  Icon: React.ComponentType;
  roles: roleType[];
  children?: MenuItem[];
}

const getMenuItems = (flags: Record<string, boolean>, roles: string[], isFronteraClarityOrg: boolean): MenuItem[] => {
  const menuItems: MenuItem[] = [];

  // Portal Home Page (PROA-1081) — the clinical landing surface. Gated behind
  // `portal-home-page` so it stays hidden until launch (mirrors the /home route
  // gating). Uses homeRolesForFlags so psychologists only see the item under
  // db-single-page (their report details require it) — keeping nav, route, and
  // redirect on the same audience. Pushed first so it sits atop Clinical Tools.
  if (flags["portal-home-page"]) {
    menuItems.push({
      path: "/home",
      name: "Home",
      Icon: HomeIcon,
      roles: homeRolesForFlags(flags),
      section: MenuSection.CLINICAL_TOOLS,
    });
  }

  // Include Clients menu item for:
  // - admin (always)
  // - psychologist / evaluation-psychologist (always — needed for File Library access)
  // - bcba / assessment-bcba (only when treatment-plan-upload or ia-to-programming is enabled)
  if (
    roles.includes("admin") ||
    roles.includes("psychologist") ||
    roles.includes("evaluation-psychologist") ||
    ((roles.includes("bcba") || roles.includes("assessment-bcba")) &&
      (flags["treatment-plan-upload"] || flags["ia-to-programming"]))
  ) {
    menuItems.push({
      path: "/clients",
      name: "Clients",
      Icon: UserGroupIcon,
      roles: roleMapping["/clients"],
      section: MenuSection.CLINICAL_TOOLS,
    });
  }

  if (flags["portal-admin-analytics"]) {
    menuItems.push({
      path: "/admin-analytics",
      name: "Analytics",
      Icon: ChartBarIcon,
      roles: roleMapping["/admin-analytics"],
      section: MenuSection.CLINICAL_TOOLS,
    });
  }

  // Hide old Evaluations nav item when db-single-page is enabled (moved to Clinical Reports)
  if (!flags["db-single-page"]) {
    menuItems.push({
      path: "/evaluations",
      name: "Evaluations",
      Icon: ClipboardIcon,
      roles: roleMapping["/evaluations"],
      section: MenuSection.CLINICAL_TOOLS,
    });
  }

  // Hide Assessment Reports nav when ia-clinical-merge is on (IA is now in Clinical Reports)
  if (!flags["ia-clinical-merge"]) {
    menuItems.push({
      path: "/assessment-reports",
      name: "Assessment Reports",
      Icon: NewspaperIcon,
      roles: roleMapping["/assessment-reports"],
      section: MenuSection.CLINICAL_TOOLS,
    });
  }

  menuItems.push(
    {
      path: "/downloads",
      name: "Assessment Templates",
      Icon: ArrowDownTrayIcon,
      roles: roleMapping["/downloads"],
      section: MenuSection.RESOURCES,
    },
    ...(flags["ia-clinical-merge"]
      ? []
      : [
          {
            path: "/assessment-reports?trigger-walkthrough=true" as AllRoutes,
            name: "Get Started",
            Icon: QuestionMarkCircleIcon,
            roles: roleMapping["/assessment-reports"],
            section: MenuSection.RESOURCES,
          },
        ]),
    {
      path: "/video-sessions",
      name: "Video Tool",
      Icon: VideoCameraIcon,
      section: MenuSection.CLINICAL_TOOLS,
      roles: [...roleMapping["/video-sessions"], ...roleMapping["/analytics"]], // or union of relevant roles
      children: [
        {
          path: "/video-sessions",
          name: "Video Sessions",
          Icon: VideoCameraIcon,
          section: MenuSection.CLINICAL_TOOLS,
          roles: roleMapping["/video-sessions"],
        },
        {
          path: "/video-sessions-v2",
          name: "Video Sessions (BETA)",
          Icon: VideoCameraIcon,
          section: MenuSection.CLINICAL_TOOLS,
          roles: roleMapping["/video-sessions"],
        },
        {
          path: "/analytics",
          // Named "Video Analytics" (not "Analytics") so it can't collide with the
          // top-level admin Analytics item — see PROA-1953, where both being named
          // "Analytics" caused the admin item to get gated by this feature's flag.
          name: "Video Analytics",
          Icon: ChartBarIcon,
          section: MenuSection.CLINICAL_TOOLS,
          roles: roleMapping["/analytics"],
        },
      ],
    },
  );

  if (isFronteraClarityOrg) {
    menuItems.push({
      path: "/frontera-clarity",
      name: "Frontera Clarity",
      Icon: ChatBubbleLeftRightIcon,
      section: MenuSection.CLINICAL_TOOLS,
      roles: [...roleMapping["/frontera-clarity"]],
      children: [
        {
          path: "/frontera-clarity/expert-chat",
          name: "Expert Chat",
          Icon: ChatBubbleLeftRightIcon,
          section: MenuSection.CLINICAL_TOOLS,
          roles: roleMapping["/frontera-clarity/expert-chat"],
        },
        {
          path: "/frontera-clarity/scheduling",
          name: "Scheduling",
          Icon: Cog8ToothIcon,
          section: MenuSection.CLINICAL_TOOLS,
          roles: roleMapping["/frontera-clarity/scheduling"],
        },
        {
          path: "/frontera-clarity/my-availability",
          name: "My Availability",
          Icon: CalendarIcon,
          section: MenuSection.CLINICAL_TOOLS,
          roles: roleMapping["/frontera-clarity/my-availability"],
        },
      ],
    });
  }

  return menuItems;
};

const filterMenuItems = (userRoles: string[], menuItems: MenuItem[]): MenuItem[] => {
  return menuItems.filter((item) => item.roles.some((role) => userRoles.includes(role)));
};

export const IndexLayout: React.FC = () => {
  const { user, isLoading, logout } = useAuth0();
  const flags = useFlags<FeatureFlags>();
  const ldClient = useLDClient();
  const ldError = useLDClientError();
  const {
    roles,
    assessmentCreditsRemaining,
    assessmentCreditsUsed,
    isSelfServeUser,
    isFetchingAssessmentCredits,
    isUserRBT,
    isUserCaregiver,
    isFronteraClarityOrg,
    loginProviderDetailsQuery,
  } = useAdminData();

  const profileImage = loginProviderDetailsQuery.data?.getLoginProvider?.profileImage;
  const resolvedImageSrc = profileImage?.signedS3ProviderFile ?? user?.picture ?? "";

  const { pathname } = useLocation();
  const go = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [settingsPanelOpen, setSettingsPanelOpen] = useState<boolean>(false);
  const [settingsPanelCollapsed, setSettingsPanelCollapsed] = useState<boolean>(false);
  const [openSubmenus, setOpenSubmenus] = useState<Set<string>>(new Set());

  const orgName = `${user![config.auth0Audience + "/orgName"] ?? ""}`;
  if (!user && !isLoading && pathname !== "/login") {
    return <Navigate to="/login" />;
  }

  const appIsBootstrapping = isLoading || (!ldClient && !ldError);

  const handleItemClick = (itemName: string) => {
    setOpenSubmenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemName)) {
        newSet.delete(itemName);
      } else {
        newSet.add(itemName);
      }
      return newSet;
    });
  };

  if (user && !isLoading && pathname === "/") {
    // Role-based post-login landing. Extracted to resolveRootRedirect so the
    // precedence (incl. the new BCBA → /home branch behind portal-home-page) is
    // unit-tested without rendering the layout.
    const landing = resolveRootRedirect({ roles, isFronteraClarityOrg, flags });
    if (landing) return <Navigate to={landing} />;
  }

  const menuItems = getMenuItems(flags as unknown as Record<string, boolean>, roles, isFronteraClarityOrg);
  if (flags["reassessment-builder"] || flags["ia-clinical-merge"]) {
    // Determine dynamic name based on user roles
    const hasPsychologistRole = roles.includes("psychologist") || roles.includes("evaluation-psychologist");
    const hasBcbaRole = roles.includes("assessment-bcba") || roles.includes("bcba");

    let clinicalReportsName = "Clinical Reports";
    let clinicalReportsRoles = roleMapping["/clinical-reports"];

    if (flags["db-single-page"]) {
      if (hasPsychologistRole && !hasBcbaRole) {
        clinicalReportsName = "Evaluations";
      } else if (hasBcbaRole && !hasPsychologistRole) {
        clinicalReportsName = flags["ia-clinical-merge"] ? "Clinical Reports" : "Reassessment";
      }
      // If both roles, keep "Clinical Reports"
    } else {
      // If db-single-page is off, only BCBA roles should see this nav item
      // Psychologists use the separate /evaluations route
      clinicalReportsName = flags["ia-clinical-merge"] && hasBcbaRole ? "Clinical Reports" : "Reassessment";
      clinicalReportsRoles = ["assessment-bcba", "bcba"];
    }

    menuItems.push({
      path: "/clinical-reports",
      name: clinicalReportsName,
      Icon: NewspaperIcon,
      roles: clinicalReportsRoles,
      section: MenuSection.CLINICAL_TOOLS,
    });
  }

  if ((roles.includes("admin") || roles.includes("frontera-internal")) && flags["portal-care-navigation"]) {
    menuItems.push({
      path: "/leads",
      name: "Leads",
      Icon: UserCircleIcon,
      roles: ["admin", "frontera-internal"],
      section: MenuSection.CLINICAL_TOOLS,
    });
    menuItems.push({
      path: "/clinics",
      name: "Clinics",
      Icon: BuildingOfficeIcon,
      roles: ["admin", "frontera-internal"],
      section: MenuSection.CLINICAL_TOOLS,
    });
  }

  const filteredMenuItems = filterMenuItems(roles, menuItems);

  const settingsMenuItems: MenuItem[] = [];

  settingsMenuItems.push({
    path: "/members",
    name: "User Management",
    Icon: UsersIcon,
    roles: roleMapping["/members"],
    section: MenuSection.ORGANIZATION_SETTINGS,
  });

  if (hasTemplatesAccess(flags)) {
    settingsMenuItems.push({
      path: "/templates",
      name: "Templates",
      Icon: DocumentDuplicateIcon,
      roles: roleMapping["/templates"],
      section: MenuSection.ORGANIZATION_SETTINGS,
    });
  }

  if (roles.includes("admin") || roles.includes("frontera-internal")) {
    settingsMenuItems.push({
      path: "/notifications/organization",
      name: "Org Notifications",
      Icon: BellAlertIcon,
      roles: roleMapping["/notifications/organization"],
      section: MenuSection.ORGANIZATION_SETTINGS,
    });
  }

  if (roles.includes("frontera-internal") && flags["qa-manager"]) {
    settingsMenuItems.push({
      path: "/qa-criteria",
      name: "Org QA Criteria",
      Icon: CheckBadgeIcon,
      roles: roleMapping["/qa-criteria"],
      section: MenuSection.ORGANIZATION_SETTINGS,
    });
  }

  if (roles.includes("admin") || roles.includes("frontera-internal")) {
    settingsMenuItems.push({
      path: "/goal-bank",
      name: "Goal Bank",
      Icon: FolderPlusIcon,
      roles: roleMapping["/goal-bank"],
      section: MenuSection.ORGANIZATION_SETTINGS,
    });
  }

  settingsMenuItems.push({
    path: "/notifications/user",
    name: "Notifications",
    Icon: BellIcon,
    roles: roleMapping["/notifications/user"],
    section: MenuSection.USER_SETTINGS,
  });

  settingsMenuItems.push({
    path: "/settings/signature",
    name: "Signature",
    Icon: PencilSquareIcon,
    roles: roleMapping["/settings/signature"],
    section: MenuSection.USER_SETTINGS,
  });

  if (flags["user-integrations"]) {
    settingsMenuItems.push({
      path: "/integrations",
      name: "Integrations",
      Icon: SquaresPlusIcon,
      roles: roleMapping["/integrations"],
      section: MenuSection.USER_SETTINGS,
    });
  }

  if (roles.includes("frontera-clarity-provider")) {
    settingsMenuItems.push({
      path: "/settings/profile",
      name: "Profile",
      Icon: UserCircleIcon,
      roles: roleMapping["/settings/profile"],
      section: MenuSection.USER_SETTINGS,
    });
  }

  const filteredSettingsMenuItems = filterMenuItems(roles, settingsMenuItems);

  let additionalMenuItems: MenuItem[] = [];
  if (filteredSettingsMenuItems.some((item) => item.path === pathname)) {
    additionalMenuItems = filteredSettingsMenuItems;
  }

  const topBarActions: TopBarAction[] = [
    {
      isVisible: true,
      content: <QuestionMarkCircleIcon className="h-6 w-6 text-text-primary"></QuestionMarkCircleIcon>,
      onAction: () => {
        window.open("https://49169539.hs-sites.com/support-hub", "_blank");
      },
      hint: "Help & Support",
    },
    {
      isVisible: filteredSettingsMenuItems.length > 0,
      content: <Cog8ToothIcon className="h-6 w-6 text-text-primary" />,
      onAction: () => {
        go(filteredSettingsMenuItems[0].path);
      },
      hint: "Settings",
    },
  ];

  const mainContent = appIsBootstrapping ? (
    <div className="flex h-screen flex-col items-center justify-center">
      <Headline size="medium" className="text-limestone-400">
        Loading...
      </Headline>
    </div>
  ) : (
    <>
      {/* Break out of SidebarLayout's two layers of horizontal padding (-mx offsets px-6+px-4/6/8)
          so the strip spans edge-to-edge. Sticky top-14 keeps it just below the h-14 header and
          moves it in document flow (no bounce), naturally pushing page content down. */}
      <div className="-mx-10 sm:-mx-12 lg:-mx-14 sticky top-14 z-[39]">
        <EhrMfaStrip />
      </div>
      {additionalMenuItems.length > 0 && (
        <>
          <button type="button" className="lg:hidden pt-3" onClick={() => setSettingsPanelOpen((prev) => !prev)}>
            <GoSidebarExpand className="w-5 h-5 text-text-secondary" />
          </button>
          <button
            type="button"
            className="hidden lg:block pt-3 cursor-pointer"
            onClick={() => setSettingsPanelCollapsed((prev) => !prev)}
          >
            {settingsPanelCollapsed ? (
              <GoSidebarCollapse className="w-5 h-5 text-text-secondary" />
            ) : (
              <GoSidebarExpand className="w-5 h-5 text-text-secondary" />
            )}
          </button>
          {settingsPanelOpen && (
            <div className="lg:hidden fixed inset-0 z-50">
              <div className="absolute inset-0 bg-black/40" onClick={() => setSettingsPanelOpen(false)} />
              <div className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-xl flex flex-col">
                <div className="flex items-center justify-between px-4 py-3 border-b border-limestone-200">
                  <Small displayType="loud">Settings</Small>
                  <button type="button" onClick={() => setSettingsPanelOpen(false)}>
                    <XMarkIcon className="h-5 w-5 text-text-primary" />
                  </button>
                </div>
                <ul className="flex-1 overflow-y-auto p-4">
                  {MenuSections.map((section) => {
                    const sectionItems = additionalMenuItems.filter((item) => item.section === section);
                    return sectionItems.length > 0 ? (
                      <div className="flex flex-col mb-2 mt-2" key={section}>
                        <Small className="mb-4" displayType="loud">
                          {section}
                        </Small>
                        {sectionItems.map((item) => (
                          <Link key={item.path} to={item.path} onClick={() => setSettingsPanelOpen(false)}>
                            <NavItem Icon={item.Icon} name={item.name} active={pathname === item.path} href="#" />
                          </Link>
                        ))}
                      </div>
                    ) : null;
                  })}
                </ul>
              </div>
            </div>
          )}
        </>
      )}
      <Outlet />
    </>
  );

  // Onboarding renders its own full-bleed shell without the sidebar chrome — the
  // user hasn't finished setup, so the nav is intentionally hidden. (Reassessment's
  // edge-to-edge layout is handled separately by the shared Layout component.)
  if (pathname === "/onboarding") {
    return <EHRConnectionProvider>{mainContent}</EHRConnectionProvider>;
  }

  return (
    <EHRConnectionProvider>
      <SidebarLayout
        invertedTheme={true}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        user={
          user
            ? {
                imageSrc: resolvedImageSrc,
                imageIsLoading: loginProviderDetailsQuery.isLoading,
                title: user.name ?? user.email ?? "",
                logout: () =>
                  logout({
                    logoutParams: {
                      returnTo: `${window.location.protocol}//${window.location.host}`,
                    },
                  }),
                orgName,
              }
            : null
        }
        sidePanelContent={
          <div className="flex flex-col justify-between size-full">
            <ul className="flex-1">
              {!user ? (
                <NavItem Icon={HomeIcon} name="Home" active={true} href="#" />
              ) : (
                <>
                  {MenuSections.filter((item) => (isUserRBT ? item !== "Resources" : item)).map((section) => {
                    const sectionItems = filteredMenuItems.filter((item) => item.section === section);
                    return sectionItems.length > 0 ? (
                      <div className="flex flex-col mb-2 mt-2" key={section}>
                        <Small className="mb-4" displayType="loud">
                          {section}
                        </Small>
                        <>
                          {sectionItems.map((item) => (
                            <MenuItemRenderer
                              key={item.name}
                              item={item}
                              isSelfServeUser={isSelfServeUser}
                              openSubmenus={openSubmenus}
                              handleItemClick={handleItemClick}
                            />
                          ))}
                        </>
                      </div>
                    ) : null;
                  })}
                </>
              )}
            </ul>
            <div className="flex space-y-4 mb-4 flex-col">
              <FeatureFlagGate flagKey="flag-viewer">
                <FeatureFlagPreviewer />
              </FeatureFlagGate>
              <FeatureFlagGate flagKey="access-token-retriever">
                <AccessTokenRetriever />
              </FeatureFlagGate>
              <FeatureFlagGate flagKey="flag-viewer">
                <EnvironmentMarker />
              </FeatureFlagGate>
            </div>
            <div>
              {isFetchingAssessmentCredits ? null : isSelfServeUser && assessmentCreditsUsed === 0 ? (
                <div className="px-4 py-2 bg-hatch-50 flex flex-col items-start rounded-md w-full">
                  <Headline size="medium">Try it for free</Headline>
                  <Small className="mt-2">Start your first Assessment Report for free by clicking "New Report"</Small>
                </div>
              ) : isSelfServeUser && assessmentCreditsUsed > 0 ? (
                <div className="px-4 py-2 bg-hatch-50 flex items-center space-x-1 rounded-md w-full">
                  <Small displayType="loud">{assessmentCreditsRemaining}</Small>
                  <Small displayType="normal">Credits Remaining</Small>

                  <Link to="/products">
                    <Button style={{ padding: 0 }} className="p-0" appearance="link" text={"Get more"} />
                  </Link>
                </div>
              ) : null}
              {isSelfServeUser && (
                <Link to="/settings">
                  <div className="hover:bg-limestone-50 text-text-primary group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 items-center justify-center mt-6 w-full">
                    <div className="w-full flex items-center">
                      <Cog6ToothIcon className="text-text-primary group-hover:text-text-primary h-6 w-6 shrink-0 mr-2" />
                      <Small displayType="loud" className="text-limestone-900">
                        Account
                      </Small>
                    </div>
                  </div>
                </Link>
              )}
            </div>
          </div>
        }
        additionalSidePanelContent={
          additionalMenuItems.length > 0 &&
          !settingsPanelCollapsed && (
            <div className="flex flex-col justify-between size-full">
              <ul className="flex-1">
                <>
                  {MenuSections.map((section) => {
                    const sectionItems = additionalMenuItems.filter((item) => item.section === section);
                    return sectionItems.length > 0 ? (
                      <div className="flex flex-col mb-2 mt-2" key={section}>
                        <Small className="mb-4" displayType="loud">
                          {section}
                        </Small>
                        <>
                          {sectionItems.map((item) => (
                            <MenuItemRenderer
                              key={item.name}
                              item={item}
                              isSelfServeUser={isSelfServeUser}
                              openSubmenus={openSubmenus}
                              handleItemClick={handleItemClick}
                            />
                          ))}
                        </>
                      </div>
                    ) : null;
                  })}
                </>
              </ul>
            </div>
          )
        }
        topBarActions={topBarActions}
        mainContent={mainContent}
        notifications={{
          isVisible: !isUserCaregiver,
          content: (
            <TooltipFocusGuard>
              <NotificationBell />
            </TooltipFocusGuard>
          ),
          onAction: () => {},
          hint: "Notifications",
        }}
        rightActions={[
          {
            isVisible: true,
            content: (
              <TooltipFocusGuard>
                <EHRConnect />
              </TooltipFocusGuard>
            ),
            onAction: () => {},
            hint: "EHR Connection",
          },
        ]}
      />
    </EHRConnectionProvider>
  );
};
